'use client';

import React from 'react';
import { Check, X, User, GraduationCap, School, MapPin } from 'lucide-react';

interface FormData {
  name: string;
  mobile: string;
  gender: string;
  teacherCategory: string;
  class: string;
  subject: string;
  currentSchoolUdise: string;
  currentSchoolName: string;
}

interface Preference {
  district: string;
  block: string;
  school: string;
  scope: 'district' | 'block' | 'school';
}

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  formData: FormData;
  preferences: Preference[];
  schoolType: 'primary' | 'other';
  isSubmitting: boolean;
}

export default function ConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  formData,
  preferences,
  schoolType,
  isSubmitting
}: ConfirmationModalProps) {
  if (!isOpen) return null;

  const getGenderDisplay = (gender: string) => {
    switch (gender) {
      case 'male': return 'Male / पुरुष';
      case 'female': return 'Female / महिला';
      case 'other': return 'Other / अन्य';
      default: return gender;
    }
  };

  const getCategoryDisplay = (category: string) => {
    switch (category) {
      case 'bpsc': return 'BPSC / बीपीएससी';
      case 'exclusive': return 'Exclusive / विशिष्ट';
      default: return category;
    }
  };

  const getClassDisplay = (classValue: string) => {
    switch (classValue) {
      case '1-5': return '1-5 (Primary / प्राथमिक)';
      case '6-8': return '6-8 (Middle / मध्य)';
      case '9-10': return '9-10 (High School / हाई स्कूल)';
      case '11-12': return '11-12 (Higher Secondary / उच्च माध्यमिक)';
      default: return classValue;
    }
  };

  const getSubjectDisplay = (subject: string, classValue?: string) => {
    if (classValue === '1-5' && subject === 'hindi') {
      return 'Hindi (General) / हिंदी (सामान्य)';
    }
    const subjectMap: { [key: string]: string } = {
      'computer': 'Computer / कंप्यूटर',
      'english': 'English / अंग्रेज़ी',
      'general': 'General / सामान्य',
      'hindi': 'Hindi / हिंदी',
      'math': 'Math / गणित',
      'sanskrit': 'Sanskrit / संस्कृत',
      'science': 'Science / विज्ञान',
      'social science': 'Social Science / सामाजिक विज्ञान',
      'urdu': 'Urdu / उर्दू'
    };
    return subjectMap[subject] || subject;
  };

  const getPreferenceScope = (pref: Preference) => {
    if (pref.scope === 'district') {
      return `${pref.district} (Any block/school / कोई भी ब्लॉक/स्कूल)`;
    } else if (pref.scope === 'block') {
      return `${pref.district} → ${pref.block} (Any school / कोई भी स्कूल)`;
    } else {
      return `${pref.district} → ${pref.block} → ${pref.school}`;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4">
      <div className="bg-white rounded-2xl shadow-2xl border border-zinc-200 max-w-2xl lg:max-w-4xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-zinc-800 via-zinc-900 to-slate-900 px-4 py-3 overflow-hidden shrink-0">
          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/30">
                <Check className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-base sm:text-lg font-bold text-white leading-tight">Confirm Your Application</h2>
                <p className="text-zinc-300 text-xs">अपने आवेदन की पुष्टि करें</p>
              </div>
            </div>
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className="p-1.5 text-white/80 hover:text-white hover:bg-white/10 rounded-full transition-all duration-200"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-3 overflow-y-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 items-start">

          {/* Personal Information */}
          <div className="bg-blue-50 rounded-lg p-2.5 border border-blue-200">
            <div className="flex items-center gap-2 mb-1.5">
              <User className="h-4 w-4 text-blue-600 shrink-0" />
              <h3 className="text-sm font-semibold text-blue-900">Personal Information / व्यक्तिगत जानकारी</h3>
            </div>
            <div className="grid grid-cols-1 gap-y-1 text-xs">
              <div><span className="font-medium text-blue-800">Name / नाम: </span><span className="text-blue-700 break-words">{formData.name}</span></div>
              <div><span className="font-medium text-blue-800">WhatsApp / व्हाट्सएप: </span><span className="text-blue-700">{formData.mobile}</span></div>
              <div><span className="font-medium text-blue-800">Gender / लिंग: </span><span className="text-blue-700">{getGenderDisplay(formData.gender)}</span></div>
              <div><span className="font-medium text-blue-800">Category / श्रेणी: </span><span className="text-blue-700">{getCategoryDisplay(formData.teacherCategory)}</span></div>
            </div>
          </div>

          {/* Professional Information */}
          <div className="bg-purple-50 rounded-lg p-2.5 border border-purple-200">
            <div className="flex items-center gap-2 mb-1.5">
              <GraduationCap className="h-4 w-4 text-purple-600 shrink-0" />
              <h3 className="text-sm font-semibold text-purple-900">Professional Information / व्यावसायिक जानकारी</h3>
            </div>
            <div className="grid grid-cols-1 gap-y-1 text-xs">
              <div><span className="font-medium text-purple-800">Class / कक्षा: </span><span className="text-purple-700">{getClassDisplay(formData.class)}</span></div>
              <div><span className="font-medium text-purple-800">Subject / विषय: </span><span className="text-purple-700">{getSubjectDisplay(formData.subject, formData.class)}</span></div>
            </div>
          </div>

          {/* Current School */}
          <div className="bg-green-50 rounded-lg p-2.5 border border-green-200 sm:col-span-2">
            <div className="flex items-center gap-2 mb-1.5">
              <School className="h-4 w-4 text-green-600 shrink-0" />
              <h3 className="text-sm font-semibold text-green-900">Current School / वर्तमान स्कूल</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs">
              <div><span className="font-medium text-green-800">School / स्कूल: </span><span className="text-green-700 break-words">{formData.currentSchoolName || 'Not specified'}</span></div>
              <div><span className="font-medium text-green-800">UDISE: </span><span className="text-green-700">{formData.currentSchoolUdise}</span></div>
            </div>
          </div>

          {/* Transfer Preferences */}
          <div className="bg-emerald-50 rounded-lg p-2.5 border border-emerald-200 sm:col-span-2">
            <div className="flex items-center gap-2 mb-1.5">
              <MapPin className="h-4 w-4 text-emerald-600 shrink-0" />
              <h3 className="text-sm font-semibold text-emerald-900">Transfer Preferences / स्थानांतरण प्राथमिकताएं</h3>
            </div>
            <div className="text-xs mb-1.5">
              <span className="font-medium text-emerald-800">School Type / स्कूल प्रकार: </span>
              <span className="text-emerald-700">{schoolType === 'primary' ? 'Primary / प्राथमिक' : 'Other / अन्य'}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
              {preferences.map((pref, idx) => (
                <div key={idx} className="flex items-start gap-2 bg-white rounded-md px-2 py-1.5 border border-emerald-200">
                  <div className="w-4 h-4 shrink-0 mt-0.5 bg-emerald-500 text-white rounded-full flex items-center justify-center text-[10px] font-bold">
                    {idx + 1}
                  </div>
                  <p className="text-emerald-700 text-xs break-words">{getPreferenceScope(pref)}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Warning */}
          <div className="bg-yellow-50 rounded-lg p-2.5 border border-yellow-200 sm:col-span-2">
            <div className="flex items-start gap-2 text-xs text-yellow-800">
              <span className="w-4 h-4 shrink-0 mt-0.5 bg-yellow-500 rounded-full flex items-center justify-center text-white text-[10px] font-bold">!</span>
              <p>Verify all details — once submitted, the application can't be edited. / सबमिट के बाद संशोधन संभव नहीं।</p>
            </div>
          </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-3 bg-zinc-50 border-t border-zinc-200 shrink-0 flex flex-col sm:flex-row-reverse gap-2">
          <button
            onClick={onConfirm}
            disabled={isSubmitting}
            className="w-full inline-flex items-center justify-center px-6 py-2.5 bg-gradient-to-r from-zinc-800 via-zinc-900 to-slate-900 text-white font-bold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Submitting... / सबमिट हो रहा है...
              </>
            ) : (
              <>
                <Check className="h-4 w-4 mr-2" />
                Submit Application / आवेदन सबमिट करें
              </>
            )}
          </button>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="w-full inline-flex items-center justify-center px-4 py-2.5 bg-white text-zinc-700 font-medium rounded-lg border border-zinc-300 hover:border-zinc-400 hover:bg-zinc-50 transition-all duration-200 disabled:opacity-50 text-sm"
          >
            Back to Edit / वापस संपादित करें
          </button>
        </div>
      </div>
    </div>
  );
} 